import {
  GlobalBanner,
  Button,
  Typography,
  Icon,
} from '@getsynapse/design-system';
import { memo, useState } from 'react';
import intl from 'react-intl-universal';
import ApprovedRequestInfoModal from './ApprovedRequestInfoModal';
import ProjectCreationModal from '../ProjectCreationModal';
import { Request } from 'utils/customTypes';
import moment from 'moment';
import { DATE } from 'utils/constants';
import classNames from 'classnames';

type Props = {
  requestData: Request;
  sidePanel?: boolean;
  dataTestId?: string;
};

const ApprovedRequestBanner = ({
  requestData,
  sidePanel,
  dataTestId,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProjectCreationModalOpen, setIsProjectCreationModalOpen] =
    useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const approvalDate = moment(
    requestData.decision_date ?? requestData.decisionDate
  ).format(DATE.SHORT_FORMAT);
  const comments = requestData.data?.approval?.comments || '';

  return (
    <>
      <ApprovedRequestInfoModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        comments={requestData.data?.approval?.comments!}
        approvalDate={approvalDate}
      />
      <ProjectCreationModal
        isOpen={isProjectCreationModalOpen}
        setModalOpen={setIsProjectCreationModalOpen}
        requestId={requestData.id!}
        requestData={requestData}
        overlayClassName={!sidePanel ? 'z-10' : 'z-50'}
        sidePanel={sidePanel}
      />
      <div
        className={classNames(sidePanel ? 'mb-4' : 'mt-2')}
        style={{
          border: '1px solid var(--primary-cog_lighter_gray_2, #E1E9EC)',
          backgroundColor: 'var(--primary-cog_white, #FFF)',
          boxShadow: '4px 0 0 0 #4B6B0A inset',
          borderRadius: '0.125rem',
          paddingLeft: '4px',
        }}
      >
        <GlobalBanner
          variant='success'
          className='bg-[var(--primary-cog_white,#FFF)] py-4'
          iconProps={{
            'data-testid': dataTestId,
          }}
        >
          {() => {
            return (
              <>
                <div className='flex justify-between items-start'>
                  <div>
                    <Typography
                      variant='body'
                      className='text-success-darkest leading-6'
                      data-testid='approved-request-banner-line'
                    >
                      {intl.getHTML(
                        'REQUEST_PAGE.APPROVED_REQUEST_BANNER.LINE_ONE',
                        { approvalDate: approvalDate }
                      )}
                    </Typography>
                    {comments.length > 0 && (
                      <div className='flex flex-col items-start'>
                        <div
                          onClick={() => {
                            setIsExpanded(!isExpanded);
                          }}
                          color='positive'
                          data-testid='view-comments-button'
                          className='flex gap-2 text-neutral-darker cursor-pointer'
                        >
                          <span>
                            {isExpanded
                              ? intl.get(
                                  'REQUEST_PAGE.APPROVED_REQUEST_BANNER.BUTTON_HIDE_COMMENTS'
                                )
                              : intl.get(
                                  'REQUEST_PAGE.APPROVED_REQUEST_BANNER.BUTTON_VIEW_COMMENTS'
                                )}
                          </span>
                          <Icon
                            name={!isExpanded ? 'caret-down' : 'caret-up'}
                            className='text-success-darkest self-center pt-1'
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      setIsProjectCreationModalOpen(true);
                    }}
                    variant='secondary'
                    data-testid='create-project-button'
                    style={{ minWidth: '123px' }}
                  >
                    {intl.get('REQUEST_PAGE.TOP_BAR.MORE.CREATE_PROJECT')}
                  </Button>
                </div>
                {isExpanded && comments.length > 0 && (
                  <div className='py-2' data-testid='expanded-comments-section'>
                    <div className='space-y-4'>
                      <div>
                        <Typography
                          variant='body2'
                          className='text-neutral-darker text-base'
                        >
                          {`“${comments}”`}
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          }}
        </GlobalBanner>
      </div>
    </>
  );
};

export default memo(ApprovedRequestBanner);
